import { useEffect, useRef, useState } from 'react';
import { Satellite } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

import { useSyncStore } from '@/stores/sync-store';
import { useAuthStore } from '@/stores/auth-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { StorageUsageIndicator } from '@/components/photos/StorageUsageIndicator';

interface SyncStatusDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Bottom-sheet drawer displaying sync status, last-sync timestamp,
 * storage usage, and a Sync Now button (Pro) or upsell (non-Pro).
 *
 * Dismisses on backdrop tap, swipe-down, or Escape key.
 */
export function SyncStatusDrawer({ open, onClose }: SyncStatusDrawerProps) {
  const { status, lastSyncedAt, error, syncNow } = useSyncStore();
  const { user } = useAuthStore();
  const isPro = useUserProfileStore((s) => s.isPro);
  const [showUpsell, setShowUpsell] = useState(false);

  const sheetRef = useRef<HTMLDivElement>(null);

  // Swipe-down to dismiss
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !open) return;

    let startY = 0;

    function handleTouchStart(e: TouchEvent) {
      startY = e.touches[0]?.clientY ?? 0;
    }

    function handleTouchEnd(e: TouchEvent) {
      const endY = e.changedTouches[0]?.clientY ?? 0;
      if (endY - startY > 60) {
        onClose();
      }
    }

    sheet.addEventListener('touchstart', handleTouchStart, { passive: true });
    sheet.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      sheet.removeEventListener('touchstart', handleTouchStart);
      sheet.removeEventListener('touchend', handleTouchEnd);
    };
  }, [open, onClose]);

  // Escape key to dismiss
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function statusLabel(): string {
    switch (status) {
      case 'syncing': return 'SYNCING';
      case 'synced': return 'SYNCED';
      case 'error': return 'ERROR';
      case 'offline': return 'OFFLINE';
      default: return 'IDLE';
    }
  }

  function lastSyncText(): string {
    if (!lastSyncedAt) return 'Never synced';
    try {
      return `Last synced ${formatDistanceToNow(parseISO(lastSyncedAt), { addSuffix: true })}`;
    } catch {
      return 'Last synced recently';
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex flex-col justify-end"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        role="presentation"
        data-testid="sync-status-drawer"
      >
        {/* Sheet */}
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sync-drawer-title"
          className="bg-[var(--color-surface-elevated)] border-t-[0.5px] border-[var(--color-border)] rounded-t-2xl px-6 pt-4 pb-10 flex flex-col gap-5"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex justify-center mb-1">
            <div
              className="w-10 h-1 rounded-full bg-[var(--color-border)]"
              aria-hidden="true"
            />
          </div>

          {/* Header */}
          <p className="field-label" id="sync-drawer-title">
            SYNC STATUS
          </p>

          {/* Status row */}
          <div className="flex items-center gap-3">
            <Satellite
              size={20}
              className={`text-[var(--color-text-muted)] shrink-0${status === 'syncing' ? ' animate-pulse' : ''}`}
              aria-hidden="true"
              data-state={status === 'syncing' ? 'syncing' : 'idle'}
            />
            <div className="flex flex-col gap-0.5">
              <p
                className="text-body-sm font-semibold text-[var(--color-text-primary)] tracking-wide"
                data-testid="sync-status-text"
              >
                {statusLabel()}
              </p>
              <p className="text-body-xs text-[var(--color-text-muted)]" data-testid="last-sync-text">
                {lastSyncText()}
              </p>
            </div>
          </div>

          {/* Error message */}
          {status === 'error' && error && (
            <p
              className="text-body-xs text-red-400 bg-red-500/10 rounded px-3 py-2"
              data-testid="sync-error-message"
            >
              {error}
            </p>
          )}

          {/* Storage usage */}
          {user && (
            <div className="flex flex-col gap-1.5">
              <p className="field-label">STORAGE</p>
              <StorageUsageIndicator userId={user.id} showBar />
            </div>
          )}

          {/* Tier-gated action */}
          {isPro() ? (
            <button
              type="button"
              data-testid="sync-now-button"
              disabled={status === 'syncing' || !user}
              onClick={() => user && void syncNow(user.id)}
              className="w-full py-3 px-4 rounded-lg border border-[var(--color-border)] text-[var(--color-text-primary)] text-body-sm font-medium transition-colors hover:bg-[var(--color-elevated)] disabled:opacity-50"
            >
              {status === 'syncing' ? 'Syncing…' : 'Sync Now'}
            </button>
          ) : (
            <button
              type="button"
              data-testid="upgrade-to-sync-button"
              onClick={() => setShowUpsell(true)}
              className="w-full py-3 px-4 rounded-lg border border-[var(--color-border)] text-[var(--color-text-muted)] text-body-sm font-medium transition-colors hover:bg-[var(--color-elevated)]"
            >
              Upgrade to Sync
            </button>
          )}
        </div>
      </div>

      {/* Upsell sheet — rendered outside the drawer backdrop so it z-indexes on top */}
      {showUpsell && (
        <UpsellSheet
          featureName={UPSELL_COPY.cloud_sync.featureName}
          description={UPSELL_COPY.cloud_sync.description}
          featureGate="cloud_sync"
          onDismiss={() => setShowUpsell(false)}
        />
      )}
    </>
  );
}
